const constants = {
    keyCodeEnter: 13,
    keyCodeBackspace: 8,
    appVersionDefault: "v.1.0.0.0",
    pingTimeoutDefault: 60000,
    defaultIndex: -1,
    defaultTenantStatus: 0,

    // HTTP status codes
    statusCode: {
        OK: 200,
        NoContent: 204,
        BadRequest: 400,
        UnauthenticatedError: 401,
        AccessDenied: 403,
        NotFound: 404,
        Conflict: 409,
        InternalServerError: 500,
        ServiceUnavailable: 503,
        NotImplemented: 501,
    },

    d365IntegrationAccountTokenUrl: "https://login.microsoftonline.com/",
    seqLogsFilter: `{0}#/events?filter=_TenantId='{1}' or TenantId='{1}'`,
    mailboxUrl: "http://outlook.office365.com/mail/",

    // Routes
    routePath: {
        root: "/",

        customers: "/customers",
        customerDetails: "/customers/:customerId/details",

        deliverables: "/deliverables",
        deliverablesPackages: "/deliverables/packages",
        deliverablesTemplates: "/deliverables/templates",

        partners: "/partners",
        partnerDetails: "/partners/:partnerId/details",

        environments: "/environments",
        storageAccounts: "/environments/:environmentId/storage-accounts",
        tenants: "/environments/:environmentId/tenants",
        tenantConfigs: "/environments/:environmentId/tenants/:tenantId",
        generalConfig:
            "/environments/:environmentId/tenants/:tenantId/general-configs",
        featureConfig:
            "/environments/:environmentId/tenants/:tenantId/feature-configs",
        erpConfig: "/environments/:environmentId/tenants/:tenantId/erp-configs",
        customFields:
            "/environments/:environmentId/tenants/:tenantId/custom-fields",
        dooapCustomFields:
            "/environments/:environmentId/tenants/:tenantId/custom-fields/dooap",
        erpCustomFields:
            "/environments/:environmentId/tenants/:tenantId/custom-fields/erp",
        studio: "/environments/:environmentId/tenants/:tenantId/studio",
        scheduler: "/environments/:environmentId/tenants/:tenantId/scheduler",
        cultureSettings:
            "/environments/:environmentId/tenants/:tenantId/culture-settings",
        dataImport:
            "/environments/:environmentId/tenants/:tenantId/data-import",
        guestTenants:
            "/environments/:environmentId/tenants/:tenantId/guest-tenants/list",
        dopeSubscriptions:
            "/environments/:environmentId/tenants/:tenantId/dope/list",
        versionHistory:
            "/environments/:environmentId/tenants/:tenantId/version-history",
        dataRefreshHistory:
            "/environments/:environmentId/tenants/:tenantId/data-refresh-history",
        maintenanceMode:
            "/environments/:environmentId/tenants/:tenantId/maintenance-mode",
        captureMailboxes:
            "/environments/:environmentId/tenants/:tenantId/mailboxes",
        activityLog:
            "/environments/:environmentId/tenants/:tenantId/activity-logs",

        enrollTenant: "/environments/:environmentId/enroll-tenant/:instanceId",
        enrollNewTenant: "/environments/:environmentId/enroll-new-tenant",
        guestTenantsEnroll:
            "/environments/:environmentId/enroll-tenant/:instanceId/guest-tenants",
        deletedTenant: "/environments/:environmentId/deleted-tenant/:tenantId",
    },
    tenantConfigsRoutes: {
        tenantConfigs: "/environments/:environmentId/tenants/:tenantId/*",
        generalConfig: "/general-configs",
        featureConfig: "feature-configs",
        erpConfig: "/erp-configs",
        customFields: "/custom-fields",
        dooapCustomFields: "/custom-fields/dooap",
        erpCustomFields: "/custom-fields/erp",
        studio: "/studio",
        scheduler: "/scheduler",
        cultureSettings: "/culture-settings",
        dataImport: "/data-import",
        guestTenants: "/guest-tenants/list",
        dopeSubscriptions: "/dope/list",
        versionHistory: "/version-history",
        dataRefreshHistory: "/data-refresh-history",
        maintenanceMode: "/maintenance-mode",
        captureMailboxes: "/mailboxes",
        activityLog: "/activity-logs",
    },
    deliverablesRoutes: {
        deliverables: "/deliverables/*",
        packages: "/packages",
        templates: "/templates",
    },
    ranges: {
        minRangeValue0: 0,
        minRangeValue1: 1,
        maxRangeValue99: 99,
        maxRangeValue100: 100,
        dotnetInt32MaxValue: 2147483647,
    },
    defaultGuid: "00000000-0000-0000-0000-000000000000",
    defaultDelay: 300,
    alertDurationMsec: 5000,
    errorAlertDurationMsec: 8000,
    maxLengthLimit: 5000,
    maxGuestTenantNameLengthLimit: 500,
    maxPartnerNameLengthLimit: 500,
    minNameLengthLimit: 3,
    itemNameLengthLimit: 30,
    enrollNewSignalRMessageMethodName: "newMessage",
    retryFailedStepEventName: "RetryFailedStep",
    completeStepEventName: "CompleteStep",
    defaultAutoTTPMaxBatchSizeValue: 500,
    userSessionIdleTimeoutInMinutes: 30,
    companyPredictionMinScoreDefaultValue: 50,
    maxCustomFieldLimit: 500,
    maxSchedulerRowsLimit: 50,
    minSchedulerIntervalLimit: 1,
    maxSchedulerIntervalValueLimit: 1000000,
    maxSchedulerIntervalDigitsLimit: 7,
    maxInvoiceRowCountInCustomField: 50,
    xmlSelectorRegex: /^(?!.*\/{3})(?!.*\s).*$/, // This regular expression allows no more than 2 '/' in a row, and does not allow whitespaces
    upperCaseRegex: /^[^a-z]*$/,
    urlRegex:
        // eslint-disable-next-line max-len
        /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i,
    emailRegex:
        // eslint-disable-next-line max-len
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    fileNameInvalidCharsRegex: /[\\\/:*?"<>| ]/g,
    versionRegex:
        /^(?:[1-9]\d*|0)\.(?:[1-9]\d*|0)\.(?:[1-9]\d*|0)\.(?:[1-9]\d*|0)$/,
    guidRegex:
        /({){0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(}){0,1}/g,
    cidrRegex: /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(\/(\d{1,2}))?$/,
    enroll: {
        // Tenant and customer info
        STEP_1: 0,

        // Guest tenants
        STEP_2: 1,

        // Integration information
        STEP_3: 2,

        // Tenant DBs ann replication
        STEP_4: 3,

        // DB level access and PowerBI account
        STEP_5: 4,

        // Set the first admin to Dooap
        STEP_6: 5,

        // Tenant Azure resources
        STEP_7: 6,

        // Setup ERP
        STEP_8: 7,

        // Alter environment DB
        STEP_9: 8,

        // Activate tenant
        STEP_10: 9,

        // Enable Search
        STEP_11: 10,

        // Data Import Control
        STEP_12: 11,

        // Initialize settings
        STEP_13: 12,

        // Activate companies
        STEP_14: 13,
    },
    applicationIdImage: "application-id.png",
    secretValueImage: "client-secret.png",
    datCompany: "DAT",
    passwordSymbolCode: 9679,
    tenantCardNameLimit: 35,
    tenantCardHeaderLimit: 45,
    customerCardNameLimit: 41,
    partnerCardHeaderLimit: 50,
    imageFormats: {
        png: "png",
        jpg: "jpg",
    },
    localizations: {
        us: "en-US",
    },
    tenantListLengths: {
        middle: 20,
        short: 15,
    },
    dimensions: {
        table: {
            maxModalHeight: 350,
            minModalHeight: 100,
            defaultModalRowHeight: 46,
            defaultRowHeight: 50,
        },
        headerHeight: 72,
        breadcrumbsHeight: 64,
        mainLayoutHeaderHeight: 142,
    },
};

Object.freeze(constants);

export default constants;
